/**
 * Static rendering for the editor canvas (ADR 0012): the same DOM builder, data binding, layout measurement and
 * frame evaluation as graphic.mjs, run synchronously on a scene that is being edited. It is a separate entry point
 * (`@prism-graphics/runtime/static`) that the exporter never bundles, so graphic.mjs does not grow.
 *
 * The caller owns fonts (FontFace objects added to document.fonts, passed as hash → family) and image URLs, so a
 * re-render after every edit is synchronous and never reloads anything.
 */
import type { Scene } from '@prism-graphics/scene-schema';
import { Renderer } from './dom.ts';
import type { EngineAction } from './engine/resolve.ts';
import { Driver, defaultData, mergeData } from './driver.ts';

export type Point = { x: number; y: number };
export type Rect = { x: number; y: number; width: number; height: number };
/** A 2D affine matrix [a, b, c, d, e, f] as in DOMMatrix / CSS matrix(). */
export type Matrix = [number, number, number, number, number, number];

export interface ElementGeometry {
  id: string;
  /** Layout box before any transform (its own or its ancestors'), in canvas px. */
  box: Rect;
  /** Maps box-local coordinates (0..box.width, 0..box.height) to canvas px, including every ancestor transform. */
  matrix: Matrix;
  /** Corners in canvas px: top-left, top-right, bottom-right, bottom-left. */
  quad: [Point, Point, Point, Point];
  /** Axis-aligned bounds of the quad. */
  bounds: Rect;
  /** Rendered (not display:none, here or in an ancestor). */
  visible: boolean;
}

export type OverflowKind = 'overflow' | 'truncated' | 'clamped';

export interface TextFit {
  id: string;
  /** Set when the text does not fit its box under the element's overflow rule. */
  problem?: OverflowKind;
  /** Natural single-line width and the available width, canvas px. */
  natural: number;
  available: number;
  /** Font size after shrink-to-fit (equals the designed size when nothing shrank). */
  fontSize: number;
}

export interface StaticFrame {
  geometry: Map<string, ElementGeometry>;
  text: TextFit[];
  /** The data the frame shows (defaults merged with the given values, normalised like load()). */
  data: Record<string, unknown>;
}

export interface StaticOptions {
  /** Field values (as in load({ data })); merged onto the field defaults. */
  data?: unknown;
  /** Font asset hash → loaded font family name. */
  families: ReadonlyMap<string, string>;
  /** Image asset hash → URL (blob:, data: or same-origin). */
  imageUrl: (hash: string) => string;
  /** 'hold': the step-0 hold (the in animation skipped to its end), 'design': the designed values only. */
  state?: 'hold' | 'design';
  /** Timeline preview: the frame at `t` of this action schedule (as goToTime after setActionsSchedule). */
  at?: { schedule: readonly ScheduleAction[]; t: number };
}

/** One entry of a preview schedule, as in OGraf setActionsSchedule (timestamps in ms). */
export interface ScheduleAction {
  t: number;
  type: EngineAction['type'];
  params?: { goto?: number; delta?: number; skipAnimation?: boolean; id?: string; data?: unknown };
}

const engineActions = (list: readonly ScheduleAction[]): EngineAction[] =>
  list.map((a, i) => ({ t: a.t, seq: i, type: a.type, params: { ...a.params }, data: a.params?.data }));

const HOLD: ScheduleAction[] = [{ t: 0, type: 'playAction', params: { skipAnimation: true } }];

const IDENTITY: Matrix = [1, 0, 0, 1, 0, 0];

export function multiply(m: Matrix, n: Matrix): Matrix {
  return [
    m[0] * n[0] + m[2] * n[1],
    m[1] * n[0] + m[3] * n[1],
    m[0] * n[2] + m[2] * n[3],
    m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4],
    m[1] * n[4] + m[3] * n[5] + m[5],
  ];
}

export function apply(m: Matrix, p: Point): Point {
  return { x: m[0] * p.x + m[2] * p.y + m[4], y: m[1] * p.x + m[3] * p.y + m[5] };
}

export function invert(m: Matrix): Matrix {
  const det = m[0] * m[3] - m[1] * m[2];
  if (Math.abs(det) < 1e-12) return IDENTITY;
  const a = m[3] / det;
  const b = -m[1] / det;
  const c = -m[2] / det;
  const d = m[0] / det;
  return [a, b, c, d, -(a * m[4] + c * m[5]), -(b * m[4] + d * m[5])];
}

const translate = (x: number, y: number): Matrix => [1, 0, 0, 1, x, y];

function parseTransform(css: string): Matrix {
  if (!css || css === 'none') return IDENTITY;
  const m = new DOMMatrixReadOnly(css);
  return [m.a, m.b, m.c, m.d, m.e, m.f];
}

/**
 * Render `scene` into `shadow` (replacing its content) and report each element's geometry and text fit.
 * The host element of `shadow` may be scaled by CSS; geometry is always in canvas px.
 */
export function renderStatic(shadow: ShadowRoot, scene: Scene, opts: StaticOptions): StaticFrame {
  const r = new Renderer(shadow, scene, new Map(opts.families), opts.imageUrl, {});
  const root = r.root;
  // The root is hidden until the first frame; measuring needs it laid out (visibility does not affect layout).
  const data = mergeData(scene.fields, defaultData(scene.fields, {}), opts.data, {});
  r.applyData(data);
  const snapshot = r.measure();

  // Layout is read in batches (all reads, then all writes), so a large scene is laid out a few times per render,
  // not once per element.
  const rootRect = root.getBoundingClientRect();
  const k = root.offsetWidth > 0 && rootRect.width > 0 ? rootRect.width / root.offsetWidth : 1;
  const boxes = new Map<string, Rect>();
  const texts: Array<{ id: string; n: typeof r.nodes extends Map<string, infer N> ? N : never; available: number }> = [];
  for (const [id, n] of r.nodes) {
    const b = n.node.getBoundingClientRect();
    boxes.set(id, { x: (b.left - rootRect.left) / k, y: (b.top - rootRect.top) / k, width: b.width / k, height: b.height / k });
    if (n.el.type === 'text' && n.node.getClientRects().length > 0) texts.push({ id, n, available: b.width / k });
  }
  // Natural single-line widths: every text at max-content at once, read, then restored.
  const saved = texts.map(({ n }) => {
    const s = n.node.style;
    const v = [s.maxWidth, s.width, s.flexShrink] as const;
    s.maxWidth = 'none';
    s.width = 'max-content';
    s.flexShrink = '0';
    return v;
  });
  const naturals = texts.map(({ n }) => n.node.getBoundingClientRect().width / k);
  texts.forEach(({ n }, i) => {
    const s = n.node.style;
    [s.maxWidth, s.width, s.flexShrink] = saved[i]!;
  });
  // Wrapped text is clamped when lifting the line clamp makes its box taller. scrollHeight is not used: with a
  // line-height below the font's ascent + descent (tight headlines), glyphs overhang the line box and one line
  // already reads as taller than its box.
  const wrapped = texts.filter(({ n }) => n.el.type === 'text' && n.el.overflow.mode === 'wrap');
  const clampedHeights = wrapped.map(({ n }) => n.node.clientHeight);
  wrapped.forEach(({ n }) => n.node.style.setProperty('-webkit-line-clamp', 'none'));
  const hiddenHeight = new Map(wrapped.map(({ id, n }, i) => [id, n.node.clientHeight - clampedHeights[i]!] as const));
  wrapped.forEach(({ n }) => {
    if (n.el.type === 'text' && n.el.overflow.mode === 'wrap') n.node.style.setProperty('-webkit-line-clamp', String(n.el.overflow.maxLines));
  });
  const text: TextFit[] = [];
  texts.forEach(({ id, n, available }, i) => {
    const el = n.el;
    if (el.type !== 'text') return;
    const natural = naturals[i]!;
    const fontSize = snapshot.fontSizes[id] ?? el.style.fontSize;
    const fit: TextFit = { id, natural, available, fontSize };
    const o = el.overflow;
    if (o.mode === 'wrap') {
      if ((hiddenHeight.get(id) ?? 0) > 1) fit.problem = 'clamped';
    } else if (natural > available + 0.5) {
      fit.problem = o.mode === 'ellipsis' ? 'truncated' : 'overflow';
    }
    text.push(fit);
  });

  // The frame: the designed values, the step-0 hold, or the timeline preview at a time.
  const driver = new Driver(r, scene, {}, data, snapshot);
  if (opts.state === 'design' && !opts.at) {
    r.applySnapshot(snapshot);
    r.invalidate();
    r.write({ visible: true, version: 0, values: new Map(), parts: new Map(), fades: new Map() });
  } else {
    driver.update(engineActions(opts.at?.schedule ?? HOLD));
    driver.render(opts.at?.t ?? 0, !opts.at);
  }
  // Layout boxes (before transforms) of the frame as written, so animated sizes count; text fit above is the
  // designed layout.
  if (opts.at) {
    const saved = [...r.nodes.values()].map((n) => [n.node, n.node.style.transform] as const);
    for (const [node] of saved) node.style.transform = 'none';
    for (const [id, n] of r.nodes) {
      const b = n.node.getBoundingClientRect();
      boxes.set(id, { x: (b.left - rootRect.left) / k, y: (b.top - rootRect.top) / k, width: b.width / k, height: b.height / k });
    }
    for (const [node, tf] of saved) node.style.transform = tf;
  }

  const geometry = new Map<string, ElementGeometry>();
  const visit = (id: string, parent: Matrix, parentVisible: boolean) => {
    const n = r.nodes.get(id);
    const box = boxes.get(id);
    if (!n || !box) return;
    const cs = getComputedStyle(n.node);
    const visible = parentVisible && cs.display !== 'none';
    const [ox = 0, oy = 0] = cs.transformOrigin.split(' ').map((v) => parseFloat(v) || 0);
    const cx = box.x + ox;
    const cy = box.y + oy;
    const own = multiply(multiply(translate(cx, cy), parseTransform(cs.transform)), translate(-cx, -cy));
    const world = multiply(parent, own);
    // Local box coordinates → canvas: translate to the box origin, then the accumulated transforms.
    const matrix = multiply(world, translate(box.x, box.y));
    const quad: [Point, Point, Point, Point] = [
      apply(matrix, { x: 0, y: 0 }),
      apply(matrix, { x: box.width, y: 0 }),
      apply(matrix, { x: box.width, y: box.height }),
      apply(matrix, { x: 0, y: box.height }),
    ];
    const xs = quad.map((p) => p.x);
    const ys = quad.map((p) => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    geometry.set(id, { id, box, matrix, quad, bounds: { x: minX, y: minY, width: Math.max(...xs) - minX, height: Math.max(...ys) - minY }, visible });
    if (n.el.type === 'group' || n.el.type === 'clipMask') for (const c of n.el.children) visit(c.id, world, visible);
  };
  for (const el of scene.elements) visit(el.id, IDENTITY, true);
  return { geometry, text, data };
}

export interface Player {
  /** Show the frame at t (ms on the schedule's clock). Cheap: no rebuild, no layout measurement. */
  seek(t: number): void;
  /** When the schedule stops changing (Infinity while a hold loop runs). */
  readonly settledAfter: number;
}

/**
 * A preview player for the editor's timeline: builds the shadow tree and resolves the schedule once, then each
 * seek only evaluates and writes a frame, exactly as graphic.mjs does in goToTime.
 */
export function createPlayer(shadow: ShadowRoot, scene: Scene, opts: StaticOptions & { schedule: readonly ScheduleAction[] }): Player {
  const r = new Renderer(shadow, scene, new Map(opts.families), opts.imageUrl, {});
  const data = mergeData(scene.fields, defaultData(scene.fields, {}), opts.data, {});
  r.applyData(data);
  const driver = new Driver(r, scene, {}, data);
  driver.update(engineActions(opts.schedule));
  return {
    seek: (t) => driver.render(t),
    settledAfter: driver.res?.settledAfter ?? 0,
  };
}
